import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, User, LayoutDashboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const links = [
  { to: "/", label: "Accueil" },
  { to: "/services", label: "Services" },
  { to: "/a-propos", label: "À propos" },
  { to: "/references", label: "Références" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 border-b border-slate-100 bg-white/85 backdrop-blur-xl">
      <div className="mx-auto max-w-7xl px-6 md:px-10 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
          <img
            src="/logo.jpg"
            alt="GIGA CLEAN SERVICE"
            className="logo-img group-hover:shadow-[0_6px_18px_rgba(33,150,243,0.45)]"
            style={{ transitionProperty: "box-shadow", transitionDuration: "300ms" }}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium link-underline transition-colors ${
                  isActive ? "text-[#2196F3]" : "text-slate-700 hover:text-[#2196F3]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-full border-slate-200" data-testid="nav-user-menu">
                  <User className="h-4 w-4 mr-2" />
                  {user.name?.split(" ")[0] || "Compte"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {user.role === "admin" ? (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="nav-menu-admin">
                    <LayoutDashboard className="h-4 w-4 mr-2" />
                    Tableau de bord
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={() => navigate("/espace-client")} data-testid="nav-menu-client">
                    <User className="h-4 w-4 mr-2" />
                    Espace client
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="nav-menu-logout">
                  <LogOut className="h-4 w-4 mr-2" />
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/connexion" data-testid="nav-login-link">
                <Button variant="ghost" className="rounded-full">Espace client</Button>
              </Link>
              <Link to="/devis" data-testid="nav-devis-cta">
                <Button className="rounded-full bg-[#2196F3] hover:bg-[#1976D2] text-white">
                  Demander un devis
                </Button>
              </Link>
            </>
          )}
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden h-11 w-11 flex items-center justify-center rounded-xl border border-slate-200"
          aria-label="Menu"
          data-testid="nav-hamburger"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-100 bg-white" data-testid="nav-mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-base font-medium ${isActive ? "text-[#2196F3]" : "text-slate-700"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="pt-4 border-t border-slate-100 flex flex-col gap-3">
              {user ? (
                <>
                  <Link to={user.role === "admin" ? "/admin" : "/espace-client"} onClick={() => setOpen(false)}>
                    <Button variant="outline" className="w-full rounded-full">
                      {user.role === "admin" ? "Tableau de bord" : "Espace client"}
                    </Button>
                  </Link>
                  <Button onClick={handleLogout} variant="ghost" className="w-full rounded-full">
                    Déconnexion
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/connexion" onClick={() => setOpen(false)}>
                    <Button variant="outline" className="w-full rounded-full">Espace client</Button>
                  </Link>
                  <Link to="/devis" onClick={() => setOpen(false)}>
                    <Button className="w-full rounded-full bg-[#2196F3] hover:bg-[#1976D2] text-white">
                      Demander un devis
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
